<template>
  <v-container>
    <h1>Leads</h1>
    <hr />

    <div class="button-group">
      <button
        class="default-btn text small minimal"
        :disabled="downloadingCsv"
        @click="requestAsFile">
        <template v-if="downloadingCsv">
          Preparing data for download
          <v-icon>mdi-loading mdi-spin</v-icon>
        </template>
        <template v-else>
          Export csv
          <v-icon>mdi-download</v-icon>
        </template>
      </button>
    </div>

    <LeadsTable
      :activeFilter.sync="activeFilter"
      :filterOptions="filterOptions"
      :leads="leads"
      :loading="loading"
      :totalItems="totalItems"
      :itemsPerPage.sync="itemsPerPage"
      :sortKey.sync="sortKey"
      :sortDesc.sync="sortDesc"
      :userSearchInput.sync="userSearchInput"
      :headers="headers"

      displayType="organization"

      @updatedFilter="getNewPage"
      @page:changed="getNewPage"
      @promptStatus="promptStatusDialog"
    />

    <DialogUpdateLeadStatus
      v-if="showStatusDialog"
      @cancel="showStatusDialog = null"
      @submit="updateMatchesStatus"
    />
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import { API_BASE_URL } from '@/config'

import DialogUpdateLeadStatus from '@/modules/shared/components/DialogUpdateLeadStatus'
import LeadsTable from '@/modules/shared/components/LeadsTable'

export default {
  name: 'OrganizationLeads',
  components: {
    DialogUpdateLeadStatus,
    LeadsTable,
  },
  props: {
    organizationId: String,
  },
  data: () => ({
    organization: {},
    leads: [],
    products: [],
    showStatusDialog: null,
    loading: false,
    downloadingCsv: false,

    tableOptions: {},
    totalItems: 0,
    sortKey: 'createdAt',
    sortDesc: true,
    itemsPerPage: 10,
    userSearchInput: '',

    headers: [
      {
        text: 'Status',
        value: 'status',
        class: 'interact column-status',
      },
      {
        text: 'Full Name',
        value: 'deal.user.fullName',
        class: 'interact',
      },
      {
        text: 'Loan Size',
        value: 'deal.dealSize',
      },
      {
        text: 'Phone Number',
        value: 'deal.user.phone',
      },
      { text: 'Email', value: 'deal.user.email' },
      { text: 'Source', value: 'deal.source' },
      { text: 'Date Created', value: 'createdAt' },
    ],

    activeFilter: {
      hot: 'all',
      product: { all: true },
      status: 'all',
    },

    filterOptions: {
      hot: [
        { key: 'all', text: 'All Users' },
        { key: 'Hot Lead' },
        { key: 'New Lead' },
      ],
      product: [
        { key: 'all', text: 'All Products' },
      ],
      status: [
        { key: 'all', text: 'Status (any)' },
        { key: 'Funded' },
        { key: 'Lending In Progress' },
        { key: 'Prospecting' },
        { key: 'Client was not responsive' },
        { key: 'Client was not a good fit/lead' },
        { key: 'Client used a different lender' },
        { key: 'Did not contact' },
      ],
    },

    filterSortQuery: {},
  }),
  computed: {
    ...mapGetters('feathers', ['apiMatches', 'apiOrganizations', 'apiProducts']),
  },

  watch: {
    userSearchInput (newVal, oldVal) {
      if (newVal !== oldVal) this.getNewPage()
    },
  },

  created () {
    this.retrieveData()
  },

  methods: {
    async retrieveData () {
      this.loading = true

      try {
        this.organization = await this.apiOrganizations.get(this.organizationId)
        this.products = await this.apiProducts.find({
          query: {
            organizationId: this.organizationId,
          },
        })

        this.buildProductFilter()
      } catch (error) {
        this.addErrorAlert(`There was an error getting the data: ${error.message}`)
      }

      await this.getNewPage()

      this.loading = false
    },

    async getNewPage (page = 1) {
      if (!this.products.length) return
      this.loading = true

      const query = {
        $limit: this.itemsPerPage || 10,
        $skip: (this.itemsPerPage || 10) * (page - 1),
        $scope: ['leadsTable', 'hideBadLeads'],
        $paginate: true,
      }

      this.filterSortQuery = {}

      if (this.userSearchInput) {
        this.filterSortQuery.$userSearch = this.userSearchInput
      }

      if (this.activeFilter.hot === 'Hot Lead') {
        this.filterSortQuery.contactedAt = { $ne: 'null' }
      }
      if (this.activeFilter.hot === 'New Lead') {
        this.filterSortQuery.contactedAt = 'null'
      }

      if (this.activeFilter.status !== 'all') {
        this.filterSortQuery.status = this.activeFilter.status
      }

      this.filterSortQuery.productId = this.activeFilter.product.all
        ? { $in: this.products.map(({ id }) => id) }
        : {
          $in: Object.entries(this.activeFilter.product)
            .filter(([_, include]) => include)
            .map(([id]) => id),
        }

      this.filterSortQuery.$sort = {
        [this.sortKey]: this.sortDesc ? -1 : 1,
      }

      try {
        const results = await this.apiMatches.find({
          query: {
            ...query,
            ...this.filterSortQuery,
          },
        })

        this.leads = (results.data || []).map(lead => ({
          ...lead,
          product: this.products.find(({ id }) => id === lead.productId),
        }))

        this.totalItems = results.total || 0
      } catch (error) {
        this.addErrorAlert(`Error retrieving leads data: ${error.message}`)
      }

      this.loading = false
    },

    buildProductFilter () {
      this.activeFilter.product = { all: true }
      this.filterOptions.product = [
        { key: 'all', text: 'All Products' },
        ...this.products.map(({ id, title, internalName }) => ({
          key: id, text: internalName || title,
        })),
      ]
    },

    promptStatusDialog (matchIds) {
      if (!matchIds) return
      if (!Array.isArray(matchIds)) matchIds = [matchIds]

      this.showStatusDialog = { matchIds }
    },

    async updateMatchesStatus (status) {
      try {
        await Promise.all(this.showStatusDialog.matchIds.map(async id => {
          await this.apiMatches.patch(id, { status })

          const index = this.leads.findIndex(match => match.id === id)

          if (index || index === 0) {
            this.leads[index].status = status
          }
        }))

        this.addSuccessAlert('Finished updating the status.')
      } catch (error) {
        this.addErrorAlert(`Could not update the status: ${error.message}`)
      }

      this.showStatusDialog = null
    },

    async requestAsFile () {
      this.downloadingCsv = true

      try {
        const data = await this.apiMatches.find({
          query: {
            ...this.filterSortQuery,
            $scope: ['leadsTable', 'hideBadLeads'],
            $contentType: 'csv',
          },
        })

        window.location.href = `${API_BASE_URL}/leads-download/?key=${data.key}`
      } catch (error) {
        this.addErrorAlert(`Could not download the csv file: ${error.message}`)
      }

      this.downloadingCsv = false
    },
  },
}
</script>

<style lang="scss" scoped>
.button-group {
  justify-content: flex-end;
  margin-right: 20px;
  margin-bottom: 10px;

  .default-btn {
    font-size: 14px;
    .v-icon { margin-left: 3px; }
  }
}

::v-deep {
  tr th, tr td {
    &:nth-child(1), &:nth-child(2) {
      padding: 0 6px !important;
    }
  }
}
</style>
